import { useCallback, useMemo } from 'react';

import { useCurrencyPreference } from '../../../shared/user-preferences/hooks/useCurrencyPreference';
import { usePageConfig } from '../../CustomDash/components/PageConfigProvider/PageConfigProvider';
import type { ReportParameterConfig } from '../../CustomDash/components/PageConfigProvider/page-reducer';
import { parametersFromPageConfig } from '../../CustomDash/components/Reports/Parameters/parameters';
import { useFilterContext } from '../../CustomDash/filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../CustomDash/filters/hooks/useEffectiveFilters';
import {
  FilterId,
  FilterType,
  type TreeSelection,
} from '../../CustomDash/filters/types/filters';
import { formatFilterDisplay } from '../../CustomDash/filters/utils/formatFilterValue';

const isFilterId = (value: unknown): value is FilterId => {
  return (
    typeof value === 'string' &&
    Object.values(FilterId).includes(value as FilterId)
  );
};

export const useReportFilters = (): {
  parameters: ReportParameterConfig[];
  additionalFilterIds: FilterId[];
  getReadableValue: ({
    parameterKey,
    filterId,
  }: {
    parameterKey?: string;
    filterId: string | FilterId;
  }) => string;
  getCurrentTreeSelections: ({
    parameterKey,
    filterId,
  }: {
    parameterKey?: string;
    filterId: string | FilterId;
  }) => TreeSelection[];
} => {
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap, filterState } = useFilterContext();
  const effectiveFilters = useEffectiveFilters();
  const { currencySymbol, exchangeRate } = useCurrencyPreference();

  const parameters = useMemo(
    () => parametersFromPageConfig(pageConfig),
    [pageConfig]
  );

  const getReadableValue = useCallback(
    ({
      parameterKey,
      filterId,
    }: {
      parameterKey?: string;
      filterId: string | FilterId;
    }): string => {
      const filterMapToUse = parameterKey
        ? filterState.groupMap.get(parameterKey)
        : activeFilterMap;
      if (!filterMapToUse) return '';
      if (!isFilterId(filterId)) return '';

      const filter = filterMapToUse.get(filterId);
      if (!filter) {
        return '';
      }

      const formatted =
        formatFilterDisplay(filter, {
          currencySymbol,
          exchangeRate,
          dateGranularity: 'month',
        }) || '';
      return formatted.trim().length > 0 ? formatted : '';
    },
    [activeFilterMap, currencySymbol, exchangeRate, filterState]
  );

  const getCurrentTreeSelections = useCallback(
    ({
      parameterKey,
      filterId,
    }: {
      parameterKey?: string;
      filterId: string | FilterId;
    }): TreeSelection[] => {
      const filterMapToUse = parameterKey
        ? filterState.groupMap.get(parameterKey)
        : activeFilterMap;
      if (!filterMapToUse) return [];
      if (!isFilterId(filterId)) return [];

      const filter = filterMapToUse.get(filterId);
      if (!filter || filter.type !== FilterType.TREE) return [];
      return filter.selections;
    },
    [activeFilterMap, filterState]
  );

  const additionalFilterIds = useMemo(() => {
    const paramIds = new Set(parameters.map((p) => String(p.filterId)));
    const additional = effectiveFilters.filter(
      (f) => !paramIds.has(String(f.id)) && f.isUserSubmitted === true
    );
    const ids = additional
      .map((f) => f.id)
      .filter(
        (fid) => getReadableValue({ filterId: String(fid) }).trim().length > 0
      );
    return ids;
  }, [parameters, effectiveFilters, getReadableValue]);

  return {
    parameters,
    additionalFilterIds,
    getReadableValue,
    getCurrentTreeSelections,
  };
};
